/**
 * Videoramme-ekstraktor for Reisefilmen.
 *
 * PROBLEM: Android System WebView kan ikke tegne et spillende `<video>` til
 * canvas (drawImage gir svart pga. hardware-overlay/hole-punch) — gjelder både
 * forhåndsvisning og eksport. LØSNING: demux MP4 (mp4box) + dekod med WebCodecs
 * `VideoDecoder` → nedskalerte `ImageBitmap`-rammer som canvas KAN tegne,
 * deterministisk (samme frame for samme tid → gyldig for offline-eksport).
 *
 * Returnerer null når WebCodecs/codec ikke støttes (f.eks. HEVC på enkelte
 * enheter) → kalleren faller tilbake til `extractFramesViaElement` (seeker et
 * skjult `<video>` og griper rammer). Uansett vei ender vi med ImageBitmaps —
 * canvas tegner ALDRI et levende `<video>` (svart på Android WebView, sent
 * ellers). mp4box importeres dynamisk (kode-splitting + unngår å laste et
 * nettleser-bibliotek i node/test).
 */

export interface ExtractedFrame { t: number; bitmap: ImageBitmap }
export interface ExtractedVideo { frames: ExtractedFrame[]; durationSec: number }

interface ExtractOpts {
  maxFps?: number;   // hvor tett vi sampler (default 12)
  maxFrames?: number;// tak på antall rammer (minne) (default 150)
  maxDim?: number;   // nedskaler lengste side (default 1080)
  clipSec?: number;  // kun de første sekundene (default 6, som resten av appen)
  onProgress?: (frac: number) => void; // dekode-fremdrift 0→1 (til innlastings-bar)
  // Kun extractFramesViaElement: tidsavbrudd for metadata-/seek-ventingen. Egen
  // knapp så tester slipper å vente på standardverdiene (8 s / 1,5 s).
  metadataTimeoutMs?: number;
  seekTimeoutMs?: number;
  // Kun extractFramesViaElement: returner null hvis ALLE rammer er ~svarte
  // (Android WebView tegner et seeket <video> svart pga. hardware-overlay) →
  // kalleren viser kartet i stedet for et svart klipp.
  rejectBlackFrames?: boolean;
}

function descriptionFromTrak(MP4Box: any, trak: any): Uint8Array | undefined {
  const entry = trak?.mdia?.minf?.stbl?.stsd?.entries?.[0];
  const box = entry && (entry.avcC || entry.hvcC || entry.vpcC || entry.av1C);
  if (!box) return undefined;
  const DataStream = MP4Box.DataStream;
  const stream = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
  box.write(stream);
  return new Uint8Array(stream.buffer, 8); // dropp 8-byte box-header
}

export async function extractVideoFrames(file: File, opts: ExtractOpts = {}): Promise<ExtractedVideo | null> {
  const dbg = (m: string) => { try { console.log(`[videoFrames] ${m}`); } catch { /* noop */ } };
  if (typeof VideoDecoder === 'undefined') { dbg('VideoDecoder undefined → null'); return null; }
  // 30 fps for jevn (ikke hakkete) avspilling — CapCut spiller video på native
  // bilderate. maxDim 810 holder minnet nede (810px-rammer ~1,5 MB mot ~8 MB på
  // full 1080; 30 fps × 6 s ≈ 180 rammer ≈ 270 MB/klipp) og gir fortsatt god
  // kvalitet skalert opp til lerretet.
  const { maxFps = 30, maxFrames = 200, maxDim = 810, clipSec = 6, onProgress } = opts;

  let MP4Box: any;
  try { MP4Box = (await import('mp4box')).default ?? (await import('mp4box')); } catch (e) { dbg('mp4box import kastet: ' + String((e as any)?.message || e)); return null; }
  if (!MP4Box?.createFile) { dbg('mp4box.createFile mangler → null'); return null; }

  // ── Demux (synkront når hele fila er bufret) ───────────────────────────────
  const mp4 = MP4Box.createFile();
  const chunks: EncodedVideoChunk[] = [];
  let setup: { codec: string; description?: Uint8Array; w: number; h: number; durationSec: number } | null = null;
  mp4.onError = (err: any) => { dbg('mp4box onError: ' + String(err)); /* setup forblir null → null returneres */ };
  mp4.onReady = (info: any) => {
    const vt = info.videoTracks && info.videoTracks[0];
    if (!vt) return;
    const trak = mp4.getTrackById(vt.id);
    const durationSec = info.duration && info.timescale ? info.duration / info.timescale
      : (vt.duration && vt.timescale ? vt.duration / vt.timescale : 0);
    setup = {
      codec: vt.codec,
      description: descriptionFromTrak(MP4Box, trak),
      w: vt.track_width || vt.video?.width || 0,
      h: vt.track_height || vt.video?.height || 0,
      durationSec,
    };
    mp4.setExtractionOptions(vt.id, null, { nbSamples: 1_000_000 });
    mp4.start();
  };
  mp4.onSamples = (_id: number, _user: any, samples: any[]) => {
    for (const s of samples) {
      chunks.push(new EncodedVideoChunk({
        type: s.is_sync ? 'key' : 'delta',
        timestamp: (s.cts / s.timescale) * 1e6,
        duration: (s.duration / s.timescale) * 1e6,
        data: s.data,
      }));
    }
  };
  try {
    const ab = await file.arrayBuffer();
    (ab as any).fileStart = 0;
    mp4.appendBuffer(ab);
    mp4.flush();
  } catch (e) { dbg('arrayBuffer/demux kastet (ulesbar fil / utløpt grant?): ' + String((e as any)?.message || e)); return null; }

  if (!setup || !chunks.length || !(setup as any).w || !(setup as any).h) {
    try { console.warn(`[videoFrames] avbrutt: setup=${!!setup} chunks=${chunks.length} dim=${(setup as any)?.w}x${(setup as any)?.h}`); } catch { /* noop */ }
    return null;
  }
  const cfg = setup as { codec: string; description?: Uint8Array; w: number; h: number; durationSec: number };

  try {
    const sup = await VideoDecoder.isConfigSupported({ codec: cfg.codec, description: cfg.description, codedWidth: cfg.w, codedHeight: cfg.h });
    try { console.log(`[videoFrames] codec=${cfg.codec} ${cfg.w}x${cfg.h} chunks=${chunks.length} støttet=${sup.supported}`); } catch { /* noop */ }
    if (!sup.supported) return null;
  } catch (e) { try { console.warn('[videoFrames] isConfigSupported kastet:', String((e as any)?.message || e)); } catch { /* noop */ } return null; }

  chunks.sort((a, b) => a.timestamp - b.timestamp);

  // ── Dekod + sample til ImageBitmap ─────────────────────────────────────────
  const frames: ExtractedFrame[] = [];
  const pending: Promise<void>[] = [];
  let lastKeptUs = -Infinity;
  let failed = false;
  let inFlight = 0; // samtidige createImageBitmap — holdes lavt (bakpress) så minnet
                    // ikke spiker; ellers FEILER senere rammer på allokering og
                    // videoen kuttes etter ~3 s (enhets-verifisert: frames dekket
                    // bare 0–3,2 s av et 6,5 s-klipp → «stopper/fryser»).
  const limitUs = clipSec * 1e6;
  // Spre `maxFrames` JEVNT over HELE klippet (opp til clipSec). Et FAST maxFps ville
  // bare dekket de første maxFrames/maxFps sekundene → videoen «fryser» på siste
  // ramme før tidsluka er ferdig. Vi utvider gapet når klippet er lengre enn
  // budsjettet rekker ved maxFps — færre fps, men dekker klippet helt.
  const clipLenUs = Math.min(limitUs, (cfg.durationSec > 0 ? cfg.durationSec : clipSec) * 1e6);
  const minGapUs = Math.max(1e6 / maxFps, clipLenUs / Math.max(1, maxFrames));

  let decoded = 0;
  const decoder = new VideoDecoder({
    output: (frame) => {
      decoded++;
      if (onProgress && chunks.length) onProgress(Math.min(1, decoded / chunks.length));
      const tUs = frame.timestamp;
      if (tUs > limitUs || frames.length + inFlight >= maxFrames || tUs < lastKeptUs + minGapUs) {
        frame.close();
        return;
      }
      lastKeptUs = tUs;
      const dw = frame.displayWidth || frame.codedWidth;
      const dh = frame.displayHeight || frame.codedHeight;
      const scale = Math.min(1, maxDim / Math.max(dw, dh));
      const rw = Math.max(2, Math.round(dw * scale));
      const rh = Math.max(2, Math.round(dh * scale));
      inFlight++;
      pending.push(
        createImageBitmap(frame, { resizeWidth: rw, resizeHeight: rh } as ImageBitmapOptions)
          .then(bmp => { frames.push({ t: tUs / 1e6, bitmap: bmp }); })
          .catch(() => { /* hopp over rammen */ })
          .finally(() => { frame.close(); inFlight--; }),
      );
    },
    error: () => { failed = true; },
  });

  // Finn siste keyframe-indeks som trengs for å dekode HELE [0, limitUs]: vi må
  // starte på en keyframe og feed alle chunks fram til (og med) den som dekker
  // limitUs. Å feed HELE (lange) klipp var en rotårsak: et 60 s / 80 MB-klipp ga
  // tusenvis av rammer i dekoderen bare for å beholde de første 6 s → minne-/
  // dekoder-kollaps → 0 ramme → «klipp kunne IKKE dekodes» (enhets-verifisert).
  let lastNeeded = chunks.length - 1;
  for (let i = 0; i < chunks.length; i++) {
    if (chunks[i].timestamp > limitUs) { lastNeeded = i; break; }
  }
  const feed = chunks.slice(0, lastNeeded + 1);

  try {
    decoder.configure({ codec: cfg.codec, description: cfg.description, codedWidth: cfg.w, codedHeight: cfg.h });
    for (const c of feed) {
      if (failed) break;
      decoder.decode(c);
      // BAKPRESS: ikke la dekoderkøen eller bitmap-pipelinen løpe løpsk. Uten
      // dette fyres alle createImageBitmap parallelt → minne-spike → senere
      // rammer feiler å allokere → klippet dekkes bare delvis (kutter/fryser).
      // Stall-vakt: synker ikke køen på ~8 s har HW-dekoderen stanset → gi opp
      // (samme klasse som eksport-enkoderens deadlock) i stedet for evig spinn.
      let waitedMs = 0;
      while (!failed && (decoder.decodeQueueSize > 8 || inFlight > 4)) {
        await new Promise(r => setTimeout(r, 4));
        if ((waitedMs += 4) > 8000) { failed = true; break; }
      }
    }
    if (!failed) await decoder.flush();
  } catch {
    failed = true;
  } finally {
    try { decoder.close(); } catch { /* allerede lukket */ }
  }
  await Promise.all(pending);

  if (!frames.length) { try { console.warn(`[videoFrames] 0 rammer beholdt (dekodet=${decoded}, failed=${failed})`); } catch { /* noop */ } return null; }
  frames.sort((a, b) => a.t - b.t);
  return { frames, durationSec: cfg.durationSec || frames[frames.length - 1].t };
}

/**
 * Leser videoens OPPTAKSDATO (ms epoch) fra MP4-metadata (mvhd `created`) via
 * mp4box. Brukes til å plassere klipp kronologisk i reisen. null hvis ukjent/
 * ugyldig → kalleren faller til File.lastModified.
 */
export async function videoCreationMs(file: File, timeoutMs = 5000): Promise<number | null> {
  let MP4Box: any;
  try { MP4Box = (await import('mp4box')).default ?? (await import('mp4box')); } catch { return null; }
  if (!MP4Box?.createFile) return null;
  return await new Promise<number | null>((resolve) => {
    const mp4 = MP4Box.createFile();
    let done = false;
    // Tidsavbrudd: mp4box fyrer noen ganger VERKEN onReady eller onError → uten dette
    // ville metadata-lesingen henge, og siden ingest nå AVVENTER den (for stabil
    // tripMedia) ville HELE innlastingen fryse (enhets-verifisert: «Laster inn …»
    // som aldri blir ferdig). Da faller vi til fil-dato/ingen dato.
    const timer = setTimeout(() => finish(null), timeoutMs);
    function finish(v: number | null) { if (!done) { done = true; clearTimeout(timer); resolve(v); } }
    mp4.onError = () => finish(null);
    mp4.onReady = (info: any) => {
      const ms = info?.created instanceof Date ? info.created.getTime() : NaN;
      // mvhd-epoken er 1904; mp4box konverterer til JS-Date. Ignorer før år 2000.
      finish(Number.isFinite(ms) && ms > 946684800000 ? ms : null);
    };
    file.arrayBuffer().then(ab => {
      (ab as any).fileStart = 0;
      try { mp4.appendBuffer(ab); mp4.flush(); } catch { finish(null); }
    }).catch(() => finish(null));
  });
}

/**
 * Rask varighet-probe (KUN mp4-metadata, ingen dekoding). Brukes til å bygge
 * reisefilm-tidslinjen UTEN å dekode alle klipp på forhånd — rammer dekodes
 * on-demand under avspilling/eksport (jevn video + bundet minne uansett antall
 * klipp). Returnerer sekunder, eller null hvis ukjent.
 */
export async function probeVideoDuration(file: File, timeoutMs = 5000): Promise<number | null> {
  let MP4Box: any;
  try { MP4Box = (await import('mp4box')).default ?? (await import('mp4box')); } catch { return null; }
  if (!MP4Box?.createFile) return null;
  return await new Promise<number | null>((resolve) => {
    const mp4 = MP4Box.createFile();
    let done = false;
    // Tidsavbrudd: mp4box fyrer noen ganger VERKEN onReady eller onError for filer
    // den ikke klarer å parse → uten dette ville probingen henge i det uendelige og
    // preview-gaten aldri åpne (enhets-verifisert ved 44 filer). Da bruker tidslinjen
    // fallback-varighet for klippet.
    const timer = setTimeout(() => finish(null), timeoutMs);
    function finish(v: number | null) { if (!done) { done = true; clearTimeout(timer); resolve(v); } }
    mp4.onError = () => finish(null);
    mp4.onReady = (info: any) => {
      const vt = info?.videoTracks && info.videoTracks[0];
      const sec = info?.duration && info?.timescale ? info.duration / info.timescale
        : (vt?.duration && vt?.timescale ? vt.duration / vt.timescale : 0);
      finish(Number.isFinite(sec) && sec > 0 ? sec : null);
    };
    file.arrayBuffer().then(ab => {
      (ab as any).fileStart = 0;
      try { mp4.appendBuffer(ab); mp4.flush(); } catch { finish(null); }
    }).catch(() => finish(null));
  });
}

/**
 * FALLBACK-ekstraktor: når WebCodecs ikke er tilgjengelig/støtter codec-en
 * (typisk iOS WKWebView + HEVC, eller desktop uten HEVC-HW), dekoder vi rammer
 * ved å SEEKE et skjult `<video>` og gripe hver frame via `drawImage` →
 * `ImageBitmap`. Da tegner forhåndsvisning OG offline-eksport ImageBitmaps —
 * ALDRI et levende `<video>` (som blir svart på Android WebView og starter
 * sent/desynket ellers → «videoer vises ikke»).
 *
 * NB: på Android System WebView kan også et seeket, pauset `<video>` gi svarte
 * rammer (hardware-overlay) — men der er WebCodecs primærveien. Denne fallbacken
 * dekker web/desktop + iOS. Returnerer null hvis rammene ikke kan dekodes (eller
 * i node/jsdom uten ekte video), så kalleren kan vise kartet i stedet for svart.
 */
export async function extractFramesViaElement(file: File, opts: ExtractOpts = {}): Promise<ExtractedVideo | null> {
  if (typeof document === 'undefined') return null;
  const {
    maxFps = 15, maxFrames = 48, maxDim = 640, clipSec = 6, onProgress,
    metadataTimeoutMs = 8000, seekTimeoutMs = 1500, rejectBlackFrames = false,
  } = opts;
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  (video as any).playsInline = true;
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.preload = 'auto';
  video.style.cssText = 'position:fixed;left:-10000px;top:0;width:2px;height:2px;opacity:0.01;pointer-events:none;';
  document.body.appendChild(video);

  const cleanup = () => {
    try { video.pause(); } catch { /* noop */ }
    video.removeAttribute('src'); try { video.load?.(); } catch { /* noop */ }
    video.remove();
    URL.revokeObjectURL(url);
  };
  const waitEvent = (evt: 'loadedmetadata' | 'seeked', ms: number): Promise<boolean> =>
    new Promise(resolve => {
      let done = false;
      const finish = (ok: boolean) => { if (done) return; done = true; video.removeEventListener(evt, on); resolve(ok); };
      const on = () => finish(true);
      video.addEventListener(evt, on, { once: true });
      setTimeout(() => finish(false), ms);
    });

  try {
    video.src = url;
    if (!(await waitEvent('loadedmetadata', metadataTimeoutMs))) { cleanup(); return null; }
    const dur = Math.min(video.duration || clipSec, clipSec);
    if (!(dur > 0) || !video.videoWidth || !video.videoHeight) { cleanup(); return null; }

    // Antall rammer: tett nok for jevn video, men innenfor minnebudsjettet.
    const count = Math.max(2, Math.min(maxFrames, Math.round(dur * maxFps)));
    const scale = Math.min(1, maxDim / Math.max(video.videoWidth, video.videoHeight));
    const cw = Math.max(2, Math.round(video.videoWidth * scale));
    const ch = Math.max(2, Math.round(video.videoHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    const cctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!cctx) { cleanup(); return null; }

    const frames: ExtractedFrame[] = [];
    let maxLuma = 0; // høyeste lysstyrke sett (Android WebView hole-punch → alt svart)
    for (let i = 0; i < count; i++) {
      const tSec = count === 1 ? 0 : (i / (count - 1)) * dur;
      video.currentTime = Math.min(tSec, Math.max(0, dur - 0.02));
      await waitEvent('seeked', seekTimeoutMs); // seek kan feile/henge på enkelte klipp — grip likevel
      if (!video.videoWidth) continue;
      try {
        cctx.drawImage(video, 0, 0, cw, ch);
        if (rejectBlackFrames) {
          const s = cctx.getImageData(0, 0, cw, ch).data; // glissen luma-sampling
          for (let p = 0; p < s.length; p += 64 * 4) {
            const l = 0.299 * s[p] + 0.587 * s[p + 1] + 0.114 * s[p + 2];
            if (l > maxLuma) maxLuma = l;
          }
        }
        const bmp = await createImageBitmap(canvas);
        frames.push({ t: tSec, bitmap: bmp });
      } catch { /* hopp over rammen */ }
      onProgress?.(Math.min(1, (i + 1) / count));
    }
    cleanup();
    if (!frames.length) return null;
    // Alle rammer ~svarte → sannsynligvis Android WebView hole-punch (kan ikke
    // tegne <video> til canvas). Kast så kalleren viser KARTET, ikke svart.
    if (rejectBlackFrames && maxLuma < 12) {
      for (const f of frames) f.bitmap.close();
      return null;
    }
    frames.sort((a, b) => a.t - b.t);
    return { frames, durationSec: dur };
  } catch {
    cleanup();
    return null;
  }
}

/** Nærmeste ramme ≤ t (sekunder). frames antas sortert stigende på t. */
export function frameAt(frames: ExtractedFrame[], t: number): ImageBitmap | null {
  if (!frames.length) return null;
  let best = frames[0].bitmap;
  for (const f of frames) {
    if (f.t <= t) best = f.bitmap;
    else break;
  }
  return best;
}
